import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PiCaretRight, PiCheck, PiUserCircle } from 'react-icons/pi';
import BottomSheet from '../BottomSheet';
import {
  MEMBER_FILTER_ALL,
  MEMBER_FILTER_UNASSIGNED,
  buildMemberFilterOptions,
  getMemberLabel,
} from '../../domain/finance/memberAttribution';
import './styles.css';

const MemberAttributionSelect = ({
  id,
  label = 'Atribuir a',
  members,
  transactions = [],
  value,
  onChange,
  filter = false,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const options = useMemo(() => {
    if (filter) return buildMemberFilterOptions(members, transactions);
    return [
      { key: '', label: 'Família / Sem atribuição' },
      ...members.map((member) => ({
        key: member.uid || member.id,
        label: getMemberLabel(member),
      })),
    ];
  }, [filter, members, transactions]);
  const selected = options.find((option) => option.key === value) || options[0];
  const selectedLabel = compact && selected.key === MEMBER_FILTER_UNASSIGNED ? 'Família' : selected.label;
  const sheetTitle = filter ? 'Filtrar por atribuição' : 'Atribuir movimentação';

  return (
    <div className={`member-attribution-select${compact ? ' is-compact' : ''}`}>
      <label className="member-attribution-select__native-label" htmlFor={id}>{label}</label>
      <select
        id={id}
        className="member-attribution-select__native"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        tabIndex={-1}
      >
        {options.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
      </select>
      <button
        type="button"
        className="member-attribution-select__trigger"
        onClick={() => setOpen(true)}
        aria-label={`${label}: ${selectedLabel}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {!compact && <span className="member-attribution-select__label">{label}</span>}
        {compact && <PiUserCircle aria-hidden="true" />}
        <strong>{selectedLabel}</strong>
        <PiCaretRight aria-hidden="true" />
      </button>

      {open && (
        <BottomSheet
          title={sheetTitle}
          subtitle={filter ? 'Mostre as movimentações de uma pessoa ou da família.' : 'Escolha quem fica associado a esta movimentação.'}
          onClose={() => setOpen(false)}
          size="sm"
          className="member-attribution-select__sheet"
        >
          <div className="member-attribution-select__options" role="radiogroup" aria-label={label}>
            {options.map((option) => (
              <button
                type="button"
                role="radio"
                aria-checked={option.key === value}
                key={option.key}
                className={option.key === value ? 'is-selected' : ''}
                onClick={() => {
                  onChange(option.key);
                  setOpen(false);
                }}
              >
                <span>{option.label}{option.removed ? ' · removido' : ''}</span>
                {option.key === value && <PiCheck aria-hidden="true" />}
              </button>
            ))}
          </div>
        </BottomSheet>
      )}
    </div>
  );
};

MemberAttributionSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  members: PropTypes.array.isRequired,
  transactions: PropTypes.array,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  filter: PropTypes.bool,
  compact: PropTypes.bool,
};

export { MEMBER_FILTER_ALL, MEMBER_FILTER_UNASSIGNED };
export default MemberAttributionSelect;
